import { EMPTY_DATA, getConsentStatusString } from "../../../constants/consent";
import { getLastCommaFirstName } from "../../../util/common";
import { deepJSONParse } from "../../../util/deepJSONParse";
import type { ITimeProvider } from "../driven/providers/provider";

export const PatientControllerFactory = ({
  patientService,
  timeProvider,
  cdlService,
}: {
  patientService: IPatientService;
  timeProvider: ITimeProvider;
  cdlService: ICDLService;
}) => {
  const getPatientSummaryProfile: IGetPatientSummaryProfile = async ({
    clinicId,
    patientId,
  }) => {
    const summary = await patientService.getPatientSummary({
      clinicId,
      patientId,
    });
    if (summary.data?.data) {
      const { resident, admission, location, alert_detail } = summary.data.data;
      const age = timeProvider
        .dateLibrary(timeProvider.dateLibrary())
        .diff(resident.dob, "years");

      return {
        success: true,
        data: {
          summary: {
            residentId: resident.id,
            firstName: resident.first_name ?? "",
            lastName: resident.last_name ?? "",
            name: getLastCommaFirstName(
              resident.first_name,
              resident.last_name
            ),
            age: isNaN(age) ? null : age,
            gender: resident.gender || "",
            consentedStatus: getConsentStatusString(admission.consented_status),
            doa: timeProvider
              .dateLibrary(admission.date_of_admission, "YYYY-MM-DD")
              ?.format("MM-DD-YYYY"),
            dob: resident.dob
              ? timeProvider
                  .dateLibrary(resident.dob, "YYYY-MM-DD")
                  ?.format("MM-DD-YYYY")
              : "-",
            level: alert_detail ? alert_detail.level : null,
            room: location.room,
            floorId: location.floor_id,
            date_of_discharge: admission.date_of_discharge
              ? timeProvider
                  .dateLibrary(admission.date_of_discharge, "YYYY-MM-DD")
                  ?.format("MM-DD-YYYY")
              : null,
          },
        },
      };
    }
    return {
      success: false,
    };
  };

  const getPatientPanelProfile: IGetPatientPanelProfile = async ({
    clinicId,
    patientId,
  }) => {
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    const summary = await patientService.getPatientSummary({
      clinicId,
      patientId,
    });

    let data = {};
    if (summary.data) {
      const { resident, admission, alert_detail, location } = summary.data.data;
      const age = timeProvider
        .dateLibrary(timeProvider.dateLibrary())
        .diff(resident.dob, "years");

      // Parse and format DOB
      const dobDateObject = resident.dob
        ? timeProvider.dateLibrary(resident.dob, "YYYY-MM-DD")
        : null;
      const formattedDob = dobDateObject?.format("MM-DD-YYYY") || "-";

      const doaDateObject = admission.date_of_admission
        ? timeProvider.dateLibrary(admission.date_of_admission, "YYYY-MM-DD")
        : null;
      const formattedDoa = doaDateObject?.format("MM-DD-YYYY") || "";

      data = {
        ...data,
        summary: {
          residentId: resident.id,
          firstName: resident.first_name ?? "",
          lastName: resident.last_name ?? "",
          name: getLastCommaFirstName(resident.first_name, resident.last_name),
          age: isNaN(age) ? "" : age,
          gender: resident.gender || "",
          consentedStatus: getConsentStatusString(admission.consented_status),
          doa: formattedDoa,
          dob: formattedDob,
          level: alert_detail ? alert_detail.level : null,
          room: location.room,
          floorId: location.floor_id,
          date_of_discharge: admission.date_of_discharge
            ? timeProvider
                .dateLibrary(admission.date_of_discharge, "YYYY-MM-DD")
                ?.format("MM-DD-YYYY")
            : null,
          hospice_status: resident.hospice_status,
          pr_ins_name: resident.pr_ins_name,
          payer_information_json: admission.payer_information_json,
          alert_flags_muted_until:
            resident.settings?.flag_preferences?.muted_until ?? null,
        },
      };

      if (admission.consented_status !== 0) {
        data = {
          ...data,
          circadiaVital: {
            rr: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            heart_rate: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            motion: {
              value: EMPTY_DATA.NA,
              date: "",
              hasReachedThreshold: false,
            },
            date: "",
          },
        };

        data = {
          ...data,
          pccVital: {
            bloodSugar: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            bloodPressure: {
              systolic: {
                value: EMPTY_DATA.NA,
                hasReachedThreshold: false,
              },
              diastolic: {
                value: EMPTY_DATA.NA,
                hasReachedThreshold: false,
              },
              time: EMPTY_DATA.NDA,
            },
            oxygenSaturation: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            heartrate: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            temperature: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
            weight: {
              value: EMPTY_DATA.NA,
              time: EMPTY_DATA.NDA,
              hasReachedThreshold: false,
            },
          },
        };
      }
    }

    return {
      success: true,
      data,
    };
  };

  function generateDateRange(
    startDateString: string,
    endDateString: string
  ): string[] {
    const startDate = timeProvider.dateLibrary(startDateString);
    const endDate = timeProvider.dateLibrary(endDateString);
    const dateRange: string[] = [];

    if (endDate.isBefore(startDate)) {
      throw new Error("End date must be greater than or equal to start date.");
    }

    let currentDate = startDate;
    while (currentDate.isSame(endDate) || currentDate.isBefore(endDate)) {
      dateRange.push(currentDate.format("YYYY-MM-DD 00:00:00"));
      currentDate = currentDate.add(1, "day");
    }

    return dateRange;
  }

  function generateHourlyDateRange(
    startDateString: string,
    endDateString: string
  ): string[] {
    const startDateTime = timeProvider.dateLibrary(startDateString);
    const endDateTime = timeProvider.dateLibrary(endDateString);
    const hourlyDateRange: string[] = [];

    if (endDateTime.isBefore(startDateTime)) {
      throw new Error("End date must be greater than or equal to start date.");
    }

    let currentDateTime = startDateTime;
    while (
      currentDateTime.isSame(endDateTime) ||
      currentDateTime.isBefore(endDateTime)
    ) {
      hourlyDateRange.push(currentDateTime.format("YYYY-MM-DD HH:mm:ss"));
      currentDateTime = currentDateTime.add(1, "hour");
    }

    return hourlyDateRange;
  }

  const getCircadiaIQ: IGetCircadiaIQController = async ({
    clinicId,
    patientId,
    shouldCall = true,
    type,
    date,
  }) => {
    if (!shouldCall) {
      return {
        error: { message: "" },
        success: false,
      };
    }

    const payload: Parameters<typeof patientService.getCircadiaIQ>[0] = {
      clinic_id: clinicId,
      patient_id: patientId,
      type,
    };

    if (date) {
      payload.date = date;
    }

    const circadiaIQData = await patientService.getCircadiaIQ(payload);

    const currentStatus = circadiaIQData.data?.data.latest_data_status;
    const status = [1, 2, 3, 4];

    if (currentStatus !== undefined && status.includes(currentStatus)) {
      if (circadiaIQData.data?.data.data !== null) {
        const dataToReturn = circadiaIQData.data
          ? (deepJSONParse(
              circadiaIQData.data!.data.data.response
            ) as unknown as {
              Insights?: string[];
              "Clinical Pathway"?: string[];
              situation?: string[];
              background?: string[];
              assessment?: {
                most_likely_conditions: string[];
                conditions_not_to_miss: string[];
                new_md_orders?: string[];
                md_np_pa_communication?: string[];
                type_of_pending_lab?: string[];
                type_of_new_or_upcoming_labs?: string[];
                medications_administered_or_new?: string[];
              };
              recommendations?: string[];
            })
          : {
              Insights: [],
              "Clinical Pathway": [],
              situation: [],
              background: [],
              assessment: {
                most_likely_conditions: [],
                conditions_not_to_miss: [],
                new_md_orders: [],
                md_np_pa_communication: [],
                type_of_pending_lab: [],
                type_of_new_or_upcoming_labs: [],
                medications_administered_or_new: [],
              },
              recommendations: [],
            };
        return {
          success: true,
          data: {
            ...circadiaIQData.data!.data,
            response: dataToReturn as unknown as {
              Insights?: string[];
              "Clinical Pathway"?: string[];
              situation?: string[];
              background?: string[];
              assessment?: {
                most_likely_conditions: string[];
                conditions_not_to_miss: string[];
              };
              recommendations?: string[];
            },
          },
        };
      }
      return {
        success: true,
        data: {
          ...circadiaIQData.data!.data,
        },
      };
    }
    return {
      error: { message: "" },
      success: false,
    };
  };

  const generateCircadiaIQ: IGenerateCircadiaIQController = async ({
    clinicId,
    patientId,
    type,
    date,
  }) => {
    const generateCircadiaIQ = await patientService.generateCircadiaIQ({
      clinic_id: clinicId,
      patient_id: patientId,
      type,
      date,
    });
    if (generateCircadiaIQ.success && generateCircadiaIQ.data) {
      return { data: generateCircadiaIQ.data, success: true };
    }

    return { error: { message: "" }, success: false };
  };

  const getPatientVitalAndRRGraph: IGetPatientVitalAndRRGraph = async ({
    clinicId,
    patientId,
    typeOfRange,
    isInitialLoaded,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }

    let dateRange = {
      to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
      from: timeProvider
        .getDateTime()
        .subtract(7, "days")
        .format("YYYY-MM-DDT00:00:00Z"),
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(7, "days")
        .format("YYYY-MM-DDT00:00:00"),
    };

    switch (typeOfRange) {
      case "4H":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(4, "hours")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(4, "hours")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "1D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(1, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(1, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "3D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(3, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(3, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;

      case "5D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(5, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(5, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      case "30D":
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(30, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(30, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
      default:
        dateRange = {
          to: timeProvider.getDateTime().format("YYYY-MM-DDTHH:mm:ssZ"),
          from: timeProvider
            .getDateTime()
            .subtract(7, "days")
            .format("YYYY-MM-DDT00:00:00Z"),
          toForRangeGenerator: timeProvider
            .getDateTime()
            .format("YYYY-MM-DDTHH:mm:ss"),
          fromforRangeGenerator: timeProvider
            .getDateTime()
            .subtract(7, "days")
            .format("YYYY-MM-DDT00:00:00"),
        };
        break;
    }

    let endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD HH:mm:ss");

    if (["30D"].includes(typeOfRange)) {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
    }

    // Add converter here
    const [vital, RR, motionBedExitData, heartRateData] = await Promise.all([
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange,
      }),

      getPatientRRGraphData({
        patientId,
        isInitialLoaded,
      }),
      getPatientMotionBedExitData({
        patientId,
        dateRange,
        isInitialLoaded,
      }),
      getPatientHeartRateData({ patientId, isInitialLoaded }),
    ]);

    if (vital.data && vital.data.data) {
      const vitalData = vital.data.data;
      let dateRangeArray: string[] = [];
      let formatToCompare = "YYYY-MM-DD";
      if (
        typeOfRange === "1D" ||
        typeOfRange === "3D" ||
        typeOfRange === "5D"
      ) {
        dateRangeArray = generateHourlyDateRange(
          dateRange.fromforRangeGenerator,
          dateRange.toForRangeGenerator
        );
        formatToCompare = "YYYY-MM-DD HH";
      } else {
        dateRangeArray = generateDateRange(
          dateRange.fromforRangeGenerator,
          dateRange.toForRangeGenerator
        );
      }

      const formattedVitals = vitalData.reduce<
        Dictionary<IPatientVitalGraph>[]
      >((prevValue, currentValue) => {
        const valueArray: number[] = [];
        const systolicValue: number[] = [];
        const dystolicValue: number[] = [];
        let objToPush: Dictionary<IPatientVitalGraph> = {};
        Object.keys(currentValue).map((key) => {
          objToPush = currentValue;
          dateRangeArray.forEach((date) => {
            const dateIndex = currentValue[key].created_at.findIndex(
              (dateValue) =>
                timeProvider.dateLibrary(dateValue).format(formatToCompare) ===
                timeProvider.dateLibrary(date).format(formatToCompare)
            );

            if (dateIndex !== -1) {
              if (key === "bloodPressure") {
                systolicValue.push(
                  currentValue[key].systolicValue![dateIndex]!
                    ? currentValue[key].systolicValue![dateIndex]!
                    : NaN
                );
                dystolicValue.push(
                  currentValue[key].diastolicValue![dateIndex]!
                    ? currentValue[key].diastolicValue![dateIndex]!
                    : NaN
                );
              } else {
                valueArray.push(
                  currentValue[key].value![dateIndex]!
                    ? currentValue[key].value![dateIndex]!
                    : NaN
                );
              }
            } else {
              valueArray.push(NaN);
              systolicValue.push(NaN);
              dystolicValue.push(NaN);
            }
          });

          objToPush[key].created_at = dateRangeArray;

          if (key === "bloodPressure") {
            objToPush[key].systolicValue = systolicValue;
            objToPush[key].diastolicValue = dystolicValue;
          } else {
            objToPush[key].value = valueArray;
          }

          return key;
        });

        prevValue.push(objToPush);
        return prevValue;
      }, [] as Dictionary<IPatientVitalGraph>[]);

      vital.data.data = formattedVitals;
    }

    if (
      vital.data &&
      RR.data &&
      motionBedExitData.data?.bedExit &&
      motionBedExitData.data?.motion &&
      heartRateData.data
    ) {
      const { motion, bedExit } = motionBedExitData.data;

      return {
        success: true,
        data: {
          vital: vital.data.data,
          RR: RR.data,
          motion,
          bedExit,
          HR: heartRateData.data,
        },
      };
    }

    return {
      success: false,
      error: { message: "Error" },
      data: {
        vital: vital?.data?.data || [],
        RR: RR?.data || {
          "1D": [],
          "3D": [],
          "5D": [],
          "30D": [],
          dailyExtra: [],
          hourlyExtra: [],
        },
        HR: heartRateData?.data || {
          "1D": [],
          "3D": [],
          "5D": [],
          "30D": [],
          dailyExtra: [],
          hourlyExtra: [],
        },
        bedExit: motionBedExitData?.data?.bedExit || [],
        motion: motionBedExitData?.data?.motion || [],
      },
    };
  };

  const getPatientVitalForLastVital: IGetPatientVitalForLastVital = async ({
    clinicId,
    patientId,
    endDate,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    if (endDate) {
      endDate = timeProvider
        .dateLibrary(endDate, "YYYY-MM-DD")
        .format("YYYY-MM-DD 23:59:59");
    } else {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
    }
    // Add converter here
    const [vital3D, vital30D] = await Promise.all([
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange: "3D",
      }),
      patientService.getPatientVitalGraph({
        clinicId,
        patientId,
        endDate,
        typeOfRange: "30D",
      }),
    ]);

    const formatter = (
      vital: typeof vital3D,
      typeOfRange: "3D" | "30D",
      dateRange: {
        toForRangeGenerator: string;
        fromforRangeGenerator: string;
      }
    ) => {
      if (vital.data && vital.data.data) {
        const vitalData = vital.data.data;
        let dateRangeArray: string[] = [];
        let formatToCompare = "YYYY-MM-DD";
        if (typeOfRange === "3D") {
          dateRangeArray = generateHourlyDateRange(
            dateRange.fromforRangeGenerator,
            dateRange.toForRangeGenerator
          );
          formatToCompare = "YYYY-MM-DD HH";
        } else {
          dateRangeArray = generateDateRange(
            dateRange.fromforRangeGenerator,
            dateRange.toForRangeGenerator
          );
        }

        const formattedVitals = vitalData.reduce<
          Dictionary<IPatientVitalGraph>[]
        >((prevValue, currentValue) => {
          const valueArray: number[] = [];
          const systolicValue: number[] = [];
          const dystolicValue: number[] = [];
          let objToPush: Dictionary<IPatientVitalGraph> = {};
          Object.keys(currentValue).map((key) => {
            objToPush = currentValue;
            dateRangeArray.forEach((date) => {
              const dateIndex = currentValue[key].created_at.findIndex(
                (dateValue) =>
                  timeProvider
                    .dateLibrary(dateValue)
                    .format(formatToCompare) ===
                  timeProvider.dateLibrary(date).format(formatToCompare)
              );

              if (dateIndex !== -1) {
                if (key === "bloodPressure") {
                  systolicValue.push(
                    currentValue[key].systolicValue![dateIndex]!
                      ? currentValue[key].systolicValue![dateIndex]!
                      : NaN
                  );
                  dystolicValue.push(
                    currentValue[key].diastolicValue![dateIndex]!
                      ? currentValue[key].diastolicValue![dateIndex]!
                      : NaN
                  );
                } else {
                  valueArray.push(
                    currentValue[key].value![dateIndex]!
                      ? currentValue[key].value![dateIndex]!
                      : NaN
                  );
                }
              } else {
                valueArray.push(NaN);
                systolicValue.push(NaN);
                dystolicValue.push(NaN);
              }
            });

            objToPush[key].created_at = dateRangeArray;

            if (key === "bloodPressure") {
              objToPush[key].systolicValue = systolicValue;
              objToPush[key].diastolicValue = dystolicValue;
            } else {
              objToPush[key].value = valueArray;
            }

            return key;
          });

          prevValue.push(objToPush);
          return prevValue;
        }, [] as Dictionary<IPatientVitalGraph>[]);

        vital.data.data = formattedVitals;
        return vital;
      }
      return vital;
    };

    const parsedVital3D = formatter(vital3D, "3D", {
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(3, "days")
        .format("YYYY-MM-DDT00:00:00"),
    });

    const parsedVital30D = formatter(vital30D, "30D", {
      toForRangeGenerator: timeProvider
        .getDateTime()
        .format("YYYY-MM-DDTHH:mm:ss"),
      fromforRangeGenerator: timeProvider
        .getDateTime()
        .subtract(30, "days")
        .format("YYYY-MM-DDT00:00:00"),
    });

    if (parsedVital3D.data && parsedVital30D.data) {
      return {
        success: true,
        data: {
          vital3D: vital3D?.data?.data || [],
          vital30D: vital30D?.data?.data || [],
        },
      };
    }

    return {
      success: false,
      error: { message: "Error" },
      data: {
        vital3D: vital3D?.data?.data || [],
        vital30D: vital30D?.data?.data || [],
      },
    };
  };

  const getPatientVitalForLastVitalForSBARVitalsTable: IGetPatientVitalForLastVitalForSBARVitalsTable =
    async ({ clinicId, patientId, endDate, shouldCall = true }) => {
      if (!shouldCall) {
        return { success: false, error: { message: "" } };
      }
      if (!patientId) {
        // Just for safe side this should not be called when used with useSWR
        throw new Error("Patient Id null");
      }
      if (endDate) {
        endDate = timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD HH:mm:ss")
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
      }
      // Add converter here
      const [vital3D] = await Promise.all([
        patientService.getPatientVitalGraph({
          clinicId,
          patientId,
          endDate,
          typeOfRange: "7D",
          hourly_format: true,
        }),
      ]);

      const formatter = (
        vital: typeof vital3D,
        typeOfRange: "7D",
        dateRange: {
          toForRangeGenerator: string;
          fromforRangeGenerator: string;
        }
      ) => {
        if (vital.data && vital.data.data) {
          const vitalData = vital.data.data;
          let dateRangeArray: string[] = [];
          let formatToCompare = "YYYY-MM-DD";
          if (typeOfRange === "7D") {
            dateRangeArray = generateHourlyDateRange(
              dateRange.fromforRangeGenerator,
              dateRange.toForRangeGenerator
            );
            formatToCompare = "YYYY-MM-DD HH";
          } else {
            dateRangeArray = generateDateRange(
              dateRange.fromforRangeGenerator,
              dateRange.toForRangeGenerator
            );
          }

          const formattedVitals = vitalData.reduce<
            Dictionary<IPatientVitalGraph>[]
          >((prevValue, currentValue) => {
            const valueArray: number[] = [];
            const systolicValue: number[] = [];
            const dystolicValue: number[] = [];
            let objToPush: Dictionary<IPatientVitalGraph> = {};
            Object.keys(currentValue).map((key) => {
              objToPush = currentValue;
              dateRangeArray.forEach((date) => {
                const dateIndex = currentValue[key].created_at.findIndex(
                  (dateValue) =>
                    timeProvider
                      .dateLibrary(dateValue)
                      .format(formatToCompare) ===
                    timeProvider.dateLibrary(date).format(formatToCompare)
                );

                if (dateIndex !== -1) {
                  if (key === "bloodPressure") {
                    systolicValue.push(
                      currentValue[key].systolicValue![dateIndex]!
                        ? currentValue[key].systolicValue![dateIndex]!
                        : NaN
                    );
                    dystolicValue.push(
                      currentValue[key].diastolicValue![dateIndex]!
                        ? currentValue[key].diastolicValue![dateIndex]!
                        : NaN
                    );
                  } else {
                    valueArray.push(
                      currentValue[key].value![dateIndex]!
                        ? currentValue[key].value![dateIndex]!
                        : NaN
                    );
                  }
                } else {
                  valueArray.push(NaN);
                  systolicValue.push(NaN);
                  dystolicValue.push(NaN);
                }
              });

              objToPush[key].created_at = dateRangeArray;

              if (key === "bloodPressure") {
                objToPush[key].systolicValue = systolicValue;
                objToPush[key].diastolicValue = dystolicValue;
              } else {
                objToPush[key].value = valueArray;
              }

              return key;
            });

            prevValue.push(objToPush);
            return prevValue;
          }, [] as Dictionary<IPatientVitalGraph>[]);

          vital.data.data = formattedVitals;
          return vital;
        }
        return vital;
      };

      const parsedVital3D = formatter(vital3D, "7D", {
        toForRangeGenerator: timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD")
          .format("YYYY-MM-DD 23:59:59"),
        fromforRangeGenerator: timeProvider
          .dateLibrary(endDate, "YYYY-MM-DD")
          .subtract(7, "days")
          .format("YYYY-MM-DDT00:00:00"),
      });

      if (parsedVital3D.data) {
        return {
          success: true,
          data: {
            vital3D: vital3D?.data?.data || [],
          },
        };
      }

      return {
        success: false,
        error: { message: "Error" },
        data: {
          vital3D: vital3D?.data?.data || [],
        },
      };
    };

  const getPatientVitalGraph: IGetPatientVitalGraph = async ({
    clinicId,
    patientId,
    typeOfRange,
    shouldCall = true,
    endDate,
    hourlyFormat = false,
  }) => {
    if (!shouldCall) {
      return { success: false, error: { message: "" } };
    }
    if (!patientId) {
      // Just for safe side this should not be called when used with useSWR
      throw new Error("Patient Id null");
    }
    if (endDate) {
      endDate = timeProvider
        .dateLibrary(endDate, "YYYY-MM-DD")
        .format("YYYY-MM-DD 23:59:59");
    } else {
      endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD HH:mm:ss");

      if (["3D", "7D", "30D"].includes(typeOfRange)) {
        endDate = timeProvider.getUTCDateTime().format("YYYY-MM-DD 23:59:59");
      }
    }

    // Add converter here
    const vital = await patientService.getPatientVitalGraph({
      clinicId,
      patientId,
      endDate,
      typeOfRange,
      hourly_format: hourlyFormat,
    });
    if (vital.data) {
      return {
        success: true,
        data: vital.data.data,
      };
    }
    return {
      success: false,
      error: { message: "Error" },
      data: [],
    };
  };

  const getPatientNotes: IGetPatientNotes = async (payload) => {
    try {
      const response = await patientService.getPatientNotes(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };
  const getPatientNotificationSettings: IGetPatientNotificationSettings =
    async (payload) => {
      try {
        const response = await patientService.getPatientNotificationSettings(
          payload
        );
        return {
          success: true,
          data: response.data,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getDiagnoses: IGetDiagnoses = async (payload) => {
    const diagnoses = await patientService.getDiagnoses(payload);

    if (diagnoses.data) {
      return {
        success: true,
        data: diagnoses.data.data,
      };
    }
    return {
      success: false,
      error: diagnoses.error,
    };
  };

  const getPCCNotes: IGetPCCNotes = async (payload) => {
    try {
      const response = await patientService.getPCCNotes(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientHmac: IGetPatientHmac = async (payload) => {
    try {
      const response = await patientService.getPatientHmac(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addPatientNote: IAddPatientNote = async ({
    clinicId,
    patientId,
    file,
    notes,
  }) => {
    const payload: Parameters<typeof patientService.addPatientNote>[0] = {
      clinicId,
      patientId,
      notes,
    };

    if (file)
      payload.file = { name: file.name, base64_enc_content: file.content };

    const { success, data, error } = await patientService.addPatientNote(
      payload
    );
    if (success && data) {
      return {
        success: true,
        data,
      };
    }
    return { success: false, error };
  };

  const getPatientRRGraphData: IGetPatientRRGraphData = async (params) => {
    if (params.isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    let startDate = timeProvider
      .getDateTime()
      .subtract(37, "days")
      .format("YYYY-MM-DDT00:00:00Z")!;
    let endDate = timeProvider.getDateTime().format("YYYY-MM-DDT23:59:59Z")!;

    if (params.endDate) {
      endDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.add(1, "hour")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;

      startDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.subtract(37, "days")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;
    }
    const response = await cdlService.getPatientGraphData({
      patient_id: params.patientId,
      dateRange: {
        from: startDate,
        to: endDate,
      },
    });
    return response;
  };

  const getPatientMotionBedExitData: IGetPatientMotionBedExitData = async ({
    patientId,
    dateRange,
    isInitialLoaded,
  }) => {
    if (!patientId) {
      return {
        success: false,
        error: { message: "Patient ID is required" },
      };
    }
    if (isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    const response = await cdlService.getBedExitMotionData({
      patient_id: patientId,
      dateRange,
    });
    return response;
  };

  const getPatientDailyRRData: IGetPatientDailyRRData = async (params) => {
    const response = await cdlService.getPatientDailyRRData({
      patient_id: params.patientId,
      dateRange: params.dateRange,
    });

    return response;
  };

  const getPatientHeartRateData: IGetPatientRRGraphData = async (params) => {
    if (params.isInitialLoaded) {
      return {
        success: false,
        error: { message: "Data already populated" },
      };
    }

    let startDate = timeProvider
      .getDateTime()
      .subtract(37, "days")
      .format("YYYY-MM-DDT00:00:00Z")!;
    let endDate = timeProvider.getDateTime().format("YYYY-MM-DDT23:59:59Z")!;

    if (params.endDate) {
      endDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.add(1, "hour")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;

      startDate = timeProvider
        .convertIntoLocalDateTime(params.endDate, "YYYY-MM-DD HH:mm:ss")
        ?.subtract(37, "days")
        ?.format("YYYY-MM-DDTHH:mm:ssZ") as string;
    }

    const response = await cdlService.getHeartRateData({
      patient_id: params.patientId,
      dateRange: {
        from: startDate,
        to: endDate,
      },
    });
    return response;
  };

  const getPatientListDashboard: IPatientListDashboard = async ({
    clinicId,
    floorIds,
    no_of_days,
  }) => {
    try {
      const response = await patientService.getPatientListDashboard({
        clinic_id: clinicId,
        floorIds,
        no_of_days: no_of_days || 5,
      });

      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    getPatientSummaryProfile,
    getPatientList: patientService.getPatientList,
    getPatientEventList: patientService.getPatientEventList,
    getPatientPanelProfile,
    getPatientVitalAndRRGraph,
    getPatientNotes,
    getPatientNotificationSettings,
    getDiagnoses,
    getPCCNotes,
    getPatientHmac,
    addPatientNote,
    getPatientRRGraphData,
    getPatientVitalGraph,
    getCircadiaIQ,
    generateCircadiaIQ,
    getPatientVitalForLastVital,
    getPatientHeartRateData,
    getPatientMotionBedExitData,
    getPatientListDashboard,
    generateDateRange,
    getPatientDailyRRData,

    getPatientVitalForLastVitalForSBARVitalsTable,
  };
};
